import { Fragment, useState, type ReactNode } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { deconnecter } from '../api/client.ts';
import { useSession } from './session.tsx';
import { Icone, type NomIcone } from './icones.tsx';
import { BoutonTheme } from './bouton-theme.tsx';
import { FournisseurApercuPdf } from './apercu-pdf.tsx';

/**
 * Ossature de l'administration.
 *
 * Le rail de navigation n'affiche que ce que les permissions autorisent. Ce
 * masquage est un CONFORT, pas une sécurité (RG-160) : le serveur refuse de
 * toute façon. Cacher un lien qui mènerait à un 403 évite simplement de
 * promener l'utilisateur dans des impasses.
 */

interface Entree {
  vers: string;
  libelle: string;
  icone: NomIcone;
  permission?: string;
  /**
   * Autres chemins pour lesquels l'entrée reste surlignée.
   *
   * « Stock » mène à `/stock`, mais ses onglets vivent sur `/receptions`,
   * `/transferts` et `/inventaires`. Sans cela, on se retrouve dans la zone
   * Stock avec AUCUNE entrée active dans le rail — et plus rien n'indique où
   * l'on est.
   */
  aussi?: string[];
}

interface Groupe {
  titre: string;
  entrees: Entree[];
}

/**
 * Navigation GROUPÉE.
 *
 * Les seize entrées étaient à plat : trouver « Transferts » obligeait à
 * relire la liste entière. Les groupes suivent le geste du patron — d'abord
 * ce qu'il consulte, puis ce qu'il vend, ce qu'il stocke, ce qu'il contrôle.
 *
 * Un groupe dont aucune entrée n'est autorisée disparaît entièrement : un
 * intitulé seul, sans rien dessous, se lirait comme un bug.
 */
const GROUPES: Groupe[] = [
  {
    titre: 'Piloter',
    entrees: [
      { vers: '/', libelle: 'Tableau de bord', icone: 'tableau', permission: 'rapport.perso.lire' },
      { vers: '/resultats', libelle: 'Résultats', icone: 'resultats', permission: 'rapport.ca.lire' },
    ],
  },
  {
    titre: 'Vendre',
    entrees: [
      { vers: '/ventes/nouvelle', libelle: 'Nouvelle vente', icone: 'panier', permission: 'vente.creer' },
      { vers: '/ventes', libelle: 'Ventes', icone: 'ventes', permission: 'vente.lire' },
      { vers: '/facturation', libelle: 'Facturation', icone: 'imprimante', permission: 'vente.lire' },
      { vers: '/clients', libelle: 'Clients et crédit', icone: 'clients', permission: 'client.lire' },
      { vers: '/retours', libelle: 'Retours', icone: 'retours', permission: 'retour.creer' },
    ],
  },
  {
    titre: 'Catalogue',
    entrees: [
      { vers: '/produits', libelle: 'Produits et prix', icone: 'produits', permission: 'produit.lire' },
      { vers: '/categories', libelle: 'Catégories', icone: 'categories', permission: 'categorie.gerer' },
      { vers: '/unites', libelle: 'Unités', icone: 'unites', permission: 'produit.lire' },
    ],
  },
  {
    titre: 'Approvisionnement',
    entrees: [
      /*
        Une seule entrée pour le stock. L'état, les réceptions, les transferts
        et les inventaires sont quatre vues d'une même question — « qu'est-ce
        que j'ai, et d'où ça vient » — et vivent désormais sous des onglets
        (voir `onglets.tsx`). Leurs URL n'ont pas changé.
      */
      {
        vers: '/stock',
        libelle: 'Stock',
        icone: 'stock',
        permission: 'stock.lire',
        aussi: ['/receptions', '/transferts', '/inventaires'],
      },
      { vers: '/fournisseurs', libelle: 'Fournisseurs', icone: 'fournisseurs', permission: 'fournisseur.lire' },
    ],
  },
  {
    titre: 'Administration',
    entrees: [
      { vers: '/utilisateurs', libelle: 'Utilisateurs', icone: 'utilisateurs', permission: 'utilisateur.lire' },
      { vers: '/boutiques', libelle: 'Boutiques', icone: 'boutiques', permission: 'boutique.lire' },
      { vers: '/journal', libelle: 'Journal d’audit', icone: 'journal', permission: 'audit.lire' },
    ],
  },
];

export function MiseEnPage(): ReactNode {
  const {
    session,
    peut,
    boutiques,
    boutiqueId,
    definirBoutique,
    boutiquesEnCours,
    erreurBoutiques,
    rechargerBoutiques,
  } = useSession();
  const naviguer = useNavigate();
  const { pathname } = useLocation();
  /**
   * Tiroir du rail, téléphone seulement (<600px, voir global.css).
   *
   * Au-dessus de 600px, cet état ne produit aucun effet visuel : les règles
   * qui en dépendent vivent toutes dans le même `@media (max-width: 600px)`.
   */
  const [menuOuvert, setMenuOuvert] = useState(false);

  if (session === null) {
    return <Navigate to="/connexion" replace />;
  }

  const groupesVisibles = GROUPES.map((groupe) => ({
    titre: groupe.titre,
    entrees: groupe.entrees.filter(
      (entree) => entree.permission === undefined || peut(entree.permission),
    ),
  })).filter((groupe) => groupe.entrees.length > 0);

  const consolide = boutiqueId === null;

  return (
    <div className="mise-en-page">
      {/*
        Barre du haut, téléphone seulement : le rail devient un tiroir hors
        champ par défaut sous 600px (voir global.css), il faut donc un moyen
        de l'ouvrir qui reste toujours visible.
      */}
      <div className="rail__entete-mobile">
        <button
          type="button"
          className="rail__bascule"
          aria-label={menuOuvert ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={menuOuvert}
          onClick={() => {
            setMenuOuvert((ouvert) => !ouvert);
          }}
        >
          <Icone nom="menu" />
        </button>
        <span>Pischon Soft</span>
      </div>

      {/* Referme le tiroir au clic en dehors — sans effet au-dessus de
          600px, où cet élément reste masqué. */}
      {menuOuvert && (
        <div
          className="rail__fond"
          onClick={() => {
            setMenuOuvert(false);
          }}
        />
      )}

      <nav
        className={menuOuvert ? 'rail rail--ouvert' : 'rail'}
        aria-label="Navigation principale"
      >
        <div className="rail__marque">
          Pischon Soft
          <span>Administration</span>
        </div>

        {groupesVisibles.map((groupe) => (
          <Fragment key={groupe.titre}>
            <div className="rail__groupe">{groupe.titre}</div>
            {groupe.entrees.map((entree) => (
              <NavLink
                key={entree.vers}
                to={entree.vers}
                end={entree.vers === '/'}
                className={({ isActive }) =>
                  isActive || (entree.aussi ?? []).includes(pathname)
                    ? 'rail__lien rail__lien--actif'
                    : 'rail__lien'
                }
                onClick={() => {
                  setMenuOuvert(false);
                }}
              >
                <Icone nom={entree.icone} />
                {entree.libelle}
              </NavLink>
            ))}
          </Fragment>
        ))}

        <div className="rail__pied">
          <span className="avatar" aria-hidden="true">
            {initiales(session.utilisateur.nom)}
          </span>
          <div style={{ minWidth: 0 }}>
            <strong>{session.utilisateur.nom}</strong>
            <span>{session.role}</span>
          </div>
          <BoutonTheme />
          <button
            type="button"
            title="Se déconnecter"
            aria-label="Se déconnecter"
            onClick={() => {
              void deconnecter().then(() => {
                // `naviguer` rend une promesse : la laisser filer masquerait
                // une navigation qui échoue.
                void naviguer('/connexion');
              });
            }}
          >
            <Icone nom="sortie" />
          </button>
        </div>
      </nav>

      <main className="contenu">
        {/*
          La boutique regardée est affichée EN PERMANENCE, en haut du contenu.
          Elle vivait auparavant dans la navigation, où on cessait de la voir.

          Lire un chiffre consolidé en croyant lire une boutique est l'erreur
          la plus coûteuse de cette application : le mode consolidé change donc
          le fond de la barre, et pas seulement son libellé. On ne remarque pas
          l'absence d'un détail ; on remarque un changement de couleur.
        */}
        {/*
          Une panne ne doit JAMAIS ressembler à une absence.

          La barre ne disparaît plus quand la liste échoue : elle dit ce qui
          s'est passé et propose de réessayer. Sans cela, `GET /boutiques` en
          erreur escamotait le sélecteur, et rien n'indiquait que la liste
          était inaccessible plutôt que vide.
        */}
        {erreurBoutiques !== null && erreurBoutiques !== undefined && (
          <div className="contexte contexte--panne">
            <span className="alert-marqueur alert-brick" aria-hidden="true" />
            <span role="alert">
              Vos boutiques n’ont pas pu être chargées. Les chiffres affichés
              sont consolidés, et vous ne pouvez pas filtrer sur une boutique.
            </span>
            <button
              type="button"
              className="bouton--discret"
              style={{ marginLeft: 'auto' }}
              onClick={rechargerBoutiques}
            >
              Réessayer
            </button>
          </div>
        )}

        {/* Le squelette réserve la place : sans lui, la page saute au moment
            où la barre apparaît. */}
        {erreurBoutiques === null && boutiquesEnCours && (
          <div className="contexte">
            <span className="squelette" style={{ width: 260, height: 20 }} />
          </div>
        )}

        {/*
          Aucune boutique dans la portée : ce n'est pas une erreur, mais le
          taire laisserait un compte devant des écrans vides sans explication.
        */}
        {erreurBoutiques === null && !boutiquesEnCours && boutiques.length === 0 && (
          <div className="contexte">
            <span className="contexte__etiquette">
              Aucune boutique ne vous est affectée. Un administrateur doit vous
              en attribuer une depuis l’écran Utilisateurs.
            </span>
          </div>
        )}

        {boutiques.length > 0 && (
          <div
            className={
              consolide && boutiques.length > 1
                ? 'contexte contexte--consolide'
                : 'contexte'
            }
          >
            <label className="contexte__etiquette" htmlFor="selecteur-boutique">
              Boutique
            </label>
            {/*
              Une seule boutique dans la portée : un sélecteur n'offrirait
              aucun choix. On affiche le nom directement. La valeur du
              contexte (`boutiqueId`) n'est pas modifiée.
            */}
            {boutiques.length === 1 && boutiques[0] !== undefined ? (
              <span className="contexte__nom" id="selecteur-boutique">
                {boutiques[0].code} — {boutiques[0].nom}
              </span>
            ) : (
            <select
              id="selecteur-boutique"
              value={boutiqueId ?? ''}
              onChange={(evenement) => {
                // Chaine vide = consolide. Un `null` ne se transporte pas
                // dans la valeur d'un <option>.
                definirBoutique(
                  evenement.target.value === '' ? null : evenement.target.value,
                );
              }}
            >
              <option value="">Toutes les boutiques</option>
              {boutiques.map((boutique) => (
                <option key={boutique.id} value={boutique.id}>
                  {boutique.code} — {boutique.nom}
                </option>
              ))}
            </select>
            )}
            {/*
              La mention n'a de sens qu'à partir de DEUX boutiques. Avec une
              seule, elle affichait « Chiffres cumulés sur 1 boutiques » —
              faux grammaticalement, et absurde sur le fond : on ne consolide
              pas une boutique unique.
            */}
            {consolide && boutiques.length > 1 && (
              <span className="contexte__note">
                Chiffres cumulés sur {boutiques.length} boutiques
              </span>
            )}
          </div>
        )}

        <FournisseurApercuPdf>
          <Outlet />
        </FournisseurApercuPdf>
      </main>
    </div>
  );
}

export function Entete({
  titre,
  sous,
  actions,
}: {
  titre: string;
  sous?: string;
  actions?: ReactNode;
}): ReactNode {
  return (
    <header className="entete">
      <div>
        <h1>{titre}</h1>
        {sous !== undefined && <div className="entete__sous">{sous}</div>}
      </div>
      {actions}
    </header>
  );
}

/** Deux lettres pour l'avatar : « Ahmed Sharif » donne « AS ». */
function initiales(nom: string): string {
  const mots = nom.trim().split(/\s+/).filter((mot) => mot !== '');
  return mots
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toUpperCase())
    .join('');
}
